import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";

const Blog = () => {
    const navigate = useNavigate();
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState({});

    useEffect(() => {
        fetchBlogPosts();
    }, []);

    const fetchBlogPosts = async () => {
        try {
            console.log('Fetching blog posts from API...');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/blog/`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });
            
            console.log('Response status:', response.status);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch blog posts: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Fetched blog posts:', data);
            
            setBlogPosts(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
            console.error('Error fetching blog posts:', err);
        }
    };

    const handlePostClick = (post) => {
        setSelectedPost(post);
        setCurrentSlideIndex({ [post.id]: 0 });
    };

    const handleCloseModal = () => {
        setSelectedPost(null);
    };

    const handleNextSlide = (postId, totalSlides) => {
        setCurrentSlideIndex(prev => ({
            ...prev,
            [postId]: (prev[postId] + 1) % totalSlides
        }));
    };

    const handlePrevSlide = (postId, totalSlides) => {
        setCurrentSlideIndex(prev => ({
            ...prev,
            [postId]: prev[postId] === 0 ? totalSlides - 1 : prev[postId] - 1
        }));
    };

    const handleContactRedirect = () => {
        navigate('/contact');
        handleCloseModal();
    };

    if (loading) {
        return (
            <>
                <HeaderOne />
                <div className="blog-container">
                    <section className="blog-hero-section">
                        <div className="hero-content">
                            <h1 className="hero-title">What's New at Panda Print</h1>
                            <p className="hero-subtitle">Loading blog posts...</p>
                        </div>
                    </section>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (error) {
        return (
            <>
                <HeaderOne />
                <div className="blog-container">
                    <section className="blog-hero-section">
                        <div className="hero-content">
                            <h1 className="hero-title">What's New at Panda Print</h1>
                            <p className="hero-subtitle" style={{ color: 'red' }}>
                                Error loading blog posts: {error}
                            </p>
                            <p style={{ fontSize: '14px', marginTop: '10px' }}>
                                Please check the browser console for more details.
                            </p>
                        </div>
                    </section>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (blogPosts.length === 0) {
        return (
            <>
                <HeaderOne />
                <div className="blog-container">
                    <section className="blog-hero-section">
                        <div className="hero-content">
                            <h1 className="hero-title">What's New at Panda Print</h1>
                            <p className="hero-subtitle">
                                No blog posts available at the moment. Check back soon!
                            </p>
                        </div>
                    </section>
                </div>
                <BottomFooter />
            </>
        );
    }

    return (
        <>
            {/* Header */}
            <HeaderOne />

            <div className="blog-container">
                {/* Hero Section */}
                <section className="blog-hero-section">
                    <div className="hero-content">
                        <h1 className="hero-title">What's New at Panda Print</h1>
                        <p className="hero-subtitle">
                            Check here for the latest news, updates, and special offers
                        </p>
                    </div>
                </section>

                {/* Main Content */}
                <section className="blog-main-content">
                    <div className="content-wrapper">
                        {/* Blog Posts Grid */}
                        <div className="blog-grid">
                            {blogPosts.map((post) => (
                                <div key={post.id} className="blog-card">
                                    <div className="blog-card-image">
                                        <img 
                                            src={post.images[0]?.image_url || 'https://via.placeholder.com/600x400?text=No+Image'} 
                                            alt={post.images[0]?.alt_text || post.title} 
                                        />
                                        <span className="category-badge">{post.category}</span>
                                    </div>
                                    <div className="blog-card-content">
                                        <div className="blog-meta">
                                            <span className="blog-date">📅 {post.formatted_date}</span>
                                        </div>
                                        <h3 className="blog-title">{post.title}</h3>
                                        <p className="blog-excerpt">{post.excerpt}</p>
                                        <button 
                                            className="read-more-btn"
                                            onClick={() => handlePostClick(post)}
                                        >
                                            Read More →
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            {/* Modal for Full Post */}
            {selectedPost && (
                <div className="blog-modal-overlay" onClick={handleCloseModal}>
                    <div className="blog-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={handleCloseModal}>✕</button>
                        
                        {/* Image Slider */}
                        {selectedPost.images && selectedPost.images.length > 0 && (
                            <div className="modal-slider">
                                <div className="slider-container">
                                    <img 
                                        src={selectedPost.images[currentSlideIndex[selectedPost.id] || 0]?.image_url} 
                                        alt={selectedPost.images[currentSlideIndex[selectedPost.id] || 0]?.alt_text || "Blog image"}
                                        className="slider-image"
                                    />
                                </div>
                                
                                {selectedPost.images.length > 1 && (
                                    <>
                                        <button 
                                            className="slider-button prev"
                                            onClick={() => handlePrevSlide(selectedPost.id, selectedPost.images.length)}
                                        >
                                            ❮
                                        </button>
                                        <button 
                                            className="slider-button next"
                                            onClick={() => handleNextSlide(selectedPost.id, selectedPost.images.length)}
                                        >
                                            ❯
                                        </button>
                                        <div className="slider-dots">
                                            {selectedPost.images.map((_, idx) => (
                                                <span 
                                                    key={idx} 
                                                    className={`dot ${idx === (currentSlideIndex[selectedPost.id] || 0) ? 'active' : ''}`}
                                                    onClick={() => setCurrentSlideIndex(prev => ({ ...prev, [selectedPost.id]: idx }))}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Modal Content */}
                        <div className="modal-content">
                            <span className="modal-category">{selectedPost.category}</span>
                            <h2 className="modal-title">{selectedPost.title}</h2>
                            <p className="modal-date">Published on {selectedPost.formatted_date}</p>
                            <div className="modal-text">
                                <p>{selectedPost.full_content}</p>
                            </div>
                            <button 
                                className="modal-cta-button"
                                onClick={handleContactRedirect}
                            >
                                Contact Us for More Details
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom Footer */}
            <BottomFooter />
        </>
    );
};

export default Blog;
